import { useState } from 'react';
import { Plus, X, DollarSign, FileCheck, Truck, Users, Map } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { ApprovalRule, ApprovalRuleType } from '@/api/services';

interface ApprovalRuleBuilderProps {
  rules: ApprovalRule[];
  onChange: (rules: ApprovalRule[]) => void;
}

const ruleTypeConfig: Record<
  ApprovalRuleType,
  { label: string; icon: React.ReactNode; description: string }
> = {
  AmountThreshold: {
    label: 'Amount Threshold',
    icon: <DollarSign className="w-4 h-4" />,
    description: 'Require approval when award amount exceeds threshold',
  },
  ContractRate: {
    label: 'Contract Rate',
    icon: <FileCheck className="w-4 h-4" />,
    description: 'Require approval for contract rate awards',
  },
  ShipmentType: {
    label: 'Shipment Type',
    icon: <Truck className="w-4 h-4" />,
    description: 'Require approval for specific shipment types',
  },
  UserBased: {
    label: 'User Based',
    icon: <Users className="w-4 h-4" />,
    description: 'Require approval for awards created by specific users',
  },
  LaneSpecific: {
    label: 'Lane Specific',
    icon: <Map className="w-4 h-4" />,
    description: 'Require approval for specific lanes',
  },
};

const shipmentTypes = [
  'OCEAN_FCL',
  'OCEAN_LCL',
  'AIR',
  'FTL',
  'LTL',
  'RAIL',
  'BREAK_BULK',
  'INTERMODAL',
];

export function ApprovalRuleBuilder({ rules, onChange }: ApprovalRuleBuilderProps) {
  const [showAddRule, setShowAddRule] = useState(false);

  const handleAddRule = (ruleType: ApprovalRuleType) => {
    const defaultCondition = getDefaultCondition(ruleType);
    const newRule: ApprovalRule = {
      ruleType,
      condition: defaultCondition,
    };
    onChange([...rules, newRule]);
    setShowAddRule(false);
  };

  const handleUpdateRule = (index: number, condition: Record<string, any>) => {
    const updated = [...rules];
    updated[index] = { ...updated[index], condition };
    onChange(updated);
  };

  const handleRemoveRule = (index: number) => {
    onChange(rules.filter((_, i) => i !== index));
  };

  const getDefaultCondition = (ruleType: ApprovalRuleType): Record<string, any> => {
    switch (ruleType) {
      case 'AmountThreshold':
        return { threshold: 10000 };
      case 'ContractRate':
        return { requiresApproval: true };
      case 'ShipmentType':
        return { shipmentTypes: [] };
      case 'UserBased':
        return { userIds: [] };
      case 'LaneSpecific':
        return { laneIds: [] };
      default:
        return {};
    }
  };

  return (
    <div className="space-y-3">
      {rules.map((rule, index) => (
        <Card key={index} className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                {ruleTypeConfig[rule.ruleType].icon}
                <span className="font-medium">{ruleTypeConfig[rule.ruleType].label}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Dismiss"
                className="h-8 w-8"
                onClick={() => handleRemoveRule(index)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <RuleConditionEditor
              ruleType={rule.ruleType}
              condition={rule.condition}
              onChange={(condition) => handleUpdateRule(index, condition)}
            />
          </CardContent>
        </Card>
      ))}

      {showAddRule ? (
        <Card className="border-dashed">
          <CardContent className="p-4">
            <Label className="mb-3 block">Select Rule Type</Label>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(ruleTypeConfig) as ApprovalRuleType[]).map((type) => {
                const config = ruleTypeConfig[type];
                const isUsed = rules.some((r) => r.ruleType === type);
                return (
                  <Button
                    key={type}
                    variant="outline"
                    className="justify-start h-auto py-3"
                    disabled={isUsed}
                    onClick={() => handleAddRule(type)}
                  >
                    <div className="flex items-start gap-3">
                      {config.icon}
                      <div className="text-left">
                        <div className="font-medium">{config.label}</div>
                        <div className="text-xs text-muted-foreground">{config.description}</div>
                      </div>
                    </div>
                  </Button>
                );
              })}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="mt-3"
              onClick={() => setShowAddRule(false)}
            >
              Cancel
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="border-dashed"
          onClick={() => setShowAddRule(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Conditional Rule
        </Button>
      )}
    </div>
  );
}

interface RuleConditionEditorProps {
  ruleType: ApprovalRuleType;
  condition: Record<string, any>;
  onChange: (condition: Record<string, any>) => void;
}

function RuleConditionEditor({ ruleType, condition, onChange }: RuleConditionEditorProps) {
  switch (ruleType) {
    case 'AmountThreshold':
      return (
        <div className="flex items-center gap-3">
          <Label className="whitespace-nowrap">Award amount exceeds:</Label>
          <div className="relative w-40">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              $
            </span>
            <Input
              type="number"
              min="0"
              step="100"
              value={condition.threshold || 0}
              onChange={(e) => onChange({ threshold: parseFloat(e.target.value) || 0 })}
              className="pl-7"
            />
          </div>
        </div>
      );

    case 'ContractRate':
      return (
        <div className="flex items-center gap-3">
          <Label className="whitespace-nowrap">Require approval for contract rates:</Label>
          <Switch
            checked={condition.requiresApproval || false}
            onCheckedChange={(checked) => onChange({ requiresApproval: checked })}
          />
        </div>
      );

    case 'ShipmentType':
      return (
        <div className="space-y-2">
          <Label>Applies to shipment types:</Label>
          <div className="flex flex-wrap gap-2">
            {shipmentTypes.map((type) => {
              const isSelected = (condition.shipmentTypes || []).includes(type);
              return (
                <Badge
                  key={type}
                  variant={isSelected ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => {
                    const current = condition.shipmentTypes || [];
                    const updated = isSelected
                      ? current.filter((t: string) => t !== type)
                      : [...current, type];
                    onChange({ shipmentTypes: updated });
                  }}
                >
                  {type.replace('_', ' ')}
                </Badge>
              );
            })}
          </div>
          {(condition.shipmentTypes || []).length === 0 && (
            <p className="text-xs text-status-warning-foreground">
              Select at least one shipment type
            </p>
          )}
        </div>
      );

    case 'UserBased':
      return (
        <div className="space-y-2">
          <Label>User IDs (comma-separated):</Label>
          <Input
            placeholder="Enter user IDs..."
            value={(condition.userIds || []).join(', ')}
            onChange={(e) =>
              onChange({
                userIds: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
          <p className="text-xs text-muted-foreground">
            Awards created by these users will require this level of approval
          </p>
        </div>
      );

    case 'LaneSpecific':
      return (
        <div className="space-y-2">
          <Label>Lane IDs (comma-separated):</Label>
          <Input
            placeholder="Enter lane IDs..."
            value={(condition.laneIds || []).join(', ')}
            onChange={(e) =>
              onChange({
                laneIds: e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
          <p className="text-xs text-muted-foreground">
            Awards for these specific lanes will require this level of approval
          </p>
        </div>
      );

    default:
      return null;
  }
}
